import React, { useEffect, useId, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/solid';
import { Icon } from '@iconify-icon/react';
import { useBool } from '#lib/hooks/use-boolean-state.js';
import { isFunction } from '#lib/utils/function.js';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Button from '#lib/components/button.jsx';
import { ImagePanel } from './images/image-panel.jsx';
import { Spinner } from '#lib/components/loading.jsx';

// FilePreview component:
// This component is used as preview of uploaded files and as button to open a modal to upload files.

export default function FilePreview(props) {
    const {
        file,
        width,
        height,
        coords,
        className,
        helperText = 'Edit image',
        defaultText = _t.global.selectImage(),
        onClear,
        children,
        rounded = false,
        onEdit,
        onChange,
        isGallery,
        isSquare
    } = props;

    const [previewFile, setPreviewFile] = useState(file);

    useEffect(() => {
        setPreviewFile(file);
    }, [file]);

    const helperId = useId();

    function handleUpdatePreview(providedFile, initCoords) {
        if (!providedFile) return;
        providedFile.coords = initCoords;
        setPreviewFile(providedFile);
        console.log('providedFile', providedFile);
        console.log('file', file);
        if ((!file || providedFile.src !== file?.src) && isFunction(onChange)) {
            const res = onChange(providedFile, initCoords);
            if (!res) setPreviewFile(null);
            return;
        }

        if (isFunction(onEdit)) {
            onEdit(initCoords);
        }
    }

    function handleClear() {
        const confirm = window.confirm(_t.global.confirmDelete());
        if (!confirm) return;
        setPreviewFile(null);
        onMouseOut();
        if (onClear) onClear();
    }

    const [isHovering, onMouseOver, onMouseOut] = useBool(false);
    const [isModalOpen, openModal, closeModal] = useBool(false);

    const mouseEvents = {
        onMouseOver,
        onMouseOut
    };

    const helperProps = {
        id: helperId,
        className: addClasses(cn(!previewFile && 'area-span-full p-3')),
        children: defaultText,
        ...mouseEvents,
        onClick: openModal,
        Icon: PhotoIcon
    };

    return (
        <div className={mergeClasses(cn('w-full h-full relative grid rounded-md p-[1px] overflow-hidden'), className)}>
            {previewFile && !previewFile.isUploading && (
                <div
                    className={`z-10 transition-all duration-300 absolute h-full w-full top-0 left-0 flex justify-center items-center bg-[#00000044] opacity-0 hover:opacity-100 text-neutral-100 cursor-pointer rounded-${
                        rounded ? 'full' : 'md'
                    }`}
                    onClick={openModal}
                >
                    <span className="flex items-end gap-2">
                        <PhotoIcon className="h-8 w-8" />
                        <span className="text-center">{helperText}</span>
                    </span>
                </div>
            )}

            <SVGBackground
                complete={!!previewFile?.src}
                appearAsButton={isHovering}
                rx={rounded ? 100 : 6}
                className={addClasses('area-span-full h-full w-full')}
            />
            {isFunction(children) && (
                <div
                    className={`area-span-full flex items-center w-full h-full overflow-hidden rounded-${
                        rounded ? 'full' : 'md'
                    }`}
                    style={isSquare || rounded ? null : { aspectRatio: '16/9' }}
                >
                    {children({
                        previewFile,
                        helperProps
                    })}
                </div>
            )}
            {previewFile && previewFile?.isUploading && (
                <div className="absolute left-0 top-0 w-full h-full flex justify-center items-center bg-[#0005] pointer-events-none">
                    <Spinner />
                </div>
            )}
            {previewFile && !previewFile.isUploading && <ClearButton onClick={handleClear} />}
            {!previewFile && <Helper {...helperProps} />}

            <ImagePanel
                file={previewFile}
                originalFile={file}
                defaultState={previewFile ? 'editing' : 'selecting'}
                isOpen={isModalOpen}
                onClose={closeModal}
                onChangePreview={handleUpdatePreview}
                width={width}
                height={height}
                coords={coords}
                isGallery={isGallery}
                multiple={false}
                isSquare={isSquare}
                rounded={rounded}
            />
        </div>
    );
}

export function ClearButton(props) {
    const { children = 'Rimuovi immagine caricata', ...rest } = props;
    return (
        <Button className={addClasses(`z-20 absolute right-2 top-2 shadow-md`)} look="circular" size="xs" {...rest}>
            <span className="sr-only">{children}</span>
            <Icon className="cursor-pointer" icon="ph:trash" width="1.2rem" height="1.2rem" />
        </Button>
    );
}

function Helper(props) {
    const { className, children, subText, Icon, onClick, ...rest } = props;

    return (
        <div
            onClick={onClick}
            className={mergeClasses(
                'h-full inline-flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md text-neutral-700 text-sm',
                className
            )}
            onMouseOver={rest.onMouseOver}
            {...rest}
        >
            <span className="flex flex-col items-center gap-1 font-semibold pointer-events-none">
                <Icon className="h-6 w-6 flex-shrink-0 -translate-x-0.5" />
                <span className="text-center">{children}</span>
            </span>
        </div>
    );
}

function SVGBackground(props) {
    const { animate, complete, appearAsButton, strokeWidth = 2, rx = 6, className, ...rest } = props;

    const rectClassName = cn(
        'transition-all animate-dash',
        appearAsButton ? 'fill-white' : 'fill-neutral-50',
        animate ? 'animation-running fill-primary-100 stroke-primary-300' : 'animation-pause stroke-neutral-300'
    );

    const isSolid = appearAsButton || (complete && !animate);

    return (
        <svg
            preserveAspectRatio="none"
            overflow="visible"
            height={rx === 100 ? '200px' : '0'}
            width={rx === 100 ? '200px' : '0'}
            className={mergeClasses(cn(appearAsButton && rx !== 100 && 'shadow-md', 'rounded-md'), className)}
            {...rest}
        >
            <rect
                className={rectClassName}
                width="100%"
                height="100%"
                style={{ '--stroke-dashoffset': strokeWidth * 6 }}
                vectorEffect="non-scaling-stroke"
                rx={rx}
                strokeWidth={appearAsButton ? 1 : strokeWidth}
                strokeDasharray={isSolid ? 'none' : strokeWidth * 3}
            />
        </svg>
    );
}

FilePreview.Helper = Helper;
